import { useEffect } from 'react';
import { AdviseCurrentSupplier, AdviseSupplierInvoice } from '../../../../types/advise';
import { TextBlock } from '../../../Atoms/Text';
import { ContractDetails } from './ContractDetails';
import { useEngagementEditor } from './EngagementEditorContext';
import { InvoiceBaselineNotice } from './InvoiceBaselineNotice';
import { InvoiceTaxesToggle } from './InvoiceTaxesToggle';
import { mostRecentInvoice } from './InvoiceView.calc';
import { ManualEntryBanner } from './ManualEntryBanner';
import { ManualEntrySwitch } from './ManualEntrySwitch';
import { ManualSpendActions } from './ManualSpendActions';
import { ManualSpendFooter } from './ManualSpendFooter';
import { ManualSpendInput } from './ManualSpendInput';
import { useManualSpendDraft } from './useManualSpendDraft';

export interface ManualSpendEntryProps {
  supplier: AdviseCurrentSupplier;
  invoice: AdviseSupplierInvoice | null;
  onChangedChange: (changed: boolean) => void;
}

export const ManualSpendEntry = ({ supplier, invoice, onChangedChange }: ManualSpendEntryProps) => {
  const { canEdit, currentSuppliers } = useEngagementEditor();
  const draft = useManualSpendDraft(supplier, invoice);
  const { changed } = draft;

  useEffect(() => {
    onChangedChange(changed);
    return () => onChangedChange(false);
  }, [changed, onChangedChange]);

  return (
    <div className="flex flex-col gap-4">
      {invoice?.status === 'needs_manual_entry' && <ManualEntryBanner supplierName={supplier.name} />}
      {invoice && draft.entry === 'line_items' && (
        // Saving line items here takes the supplier off its monthly total, so the notice follows the draft.
        <InvoiceBaselineNotice
          supplier={{ ...supplier, spend_entry: 'line_items' }}
          invoice={invoice}
        />
      )}
      <ManualSpendInput
        supplier={supplier}
        invoice={invoice}
        draft={draft}
      />
      {canEdit && (
        <ManualEntrySwitch
          draft={draft}
          countLinesSupplierId={!invoice && supplier.invoices.length > 0 ? supplier.id : undefined}
        />
      )}
      {invoice && draft.entry === 'line_items' && (
        <InvoiceTaxesToggle
          taxes={(mostRecentInvoice(supplier) ?? invoice).taxes_and_surcharges}
          included={supplier.include_taxes}
          canEdit={canEdit}
          onChange={(included) => currentSuppliers?.setIncludeTaxes(supplier.id, included)}
        />
      )}
      {draft.entry === 'line_items' && draft.error && (
        <TextBlock
          size="xs"
          color="red">
          {draft.error}
        </TextBlock>
      )}
      <div className="border-t border-grey-2 pt-4">
        <ContractDetails
          contract={supplier.contract}
          canEdit={canEdit}
          onSave={(contract) => currentSuppliers?.saveContract(supplier.id, contract)}
        />
      </div>
      <ManualSpendFooter
        supplier={supplier}
        invoice={invoice}
        draft={draft}
      />
      {canEdit && <ManualSpendActions draft={draft} />}
    </div>
  );
};
